/**
 * countdown-timer.js
 * Web Component for the Countdown Timer block.
 * Counts down to a deadline in Pacific/Auckland time.
 * No dependencies — works standalone alongside Horizon's component system.
 */

const TZ = 'Pacific/Auckland';
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/**
 * Convert a date string (YYYY-MM-DD) + time (h, m, s) expressed in NZ local
 * time into a UTC Date object, correctly handling NZST/NZDT offsets.
 */
function nzToUTC(nzDateStr, h, m, s) {
  const pad = n => String(n).padStart(2, '0');

  // Probe: treat the NZ wall-clock time as if it were UTC
  const probe = new Date(`${nzDateStr}T${pad(h)}:${pad(m)}:${pad(s)}Z`);

  // Ask Intl what NZ local time that UTC instant represents
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(probe);

  const get = type => parts.find(p => p.type === type).value;
  const nzInterpretedAsUTC = new Date(
    `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`
  );

  // The real UTC instant = probe shifted by the NZ offset
  return new Date(probe.getTime() + (probe.getTime() - nzInterpretedAsUTC.getTime()));
}

/**
 * Find the next occurrence of `targetDow` (0 = Sun … 6 = Sat) at h:m:s
 * in Pacific/Auckland time, starting from tomorrow.
 */
function nextNZDeadline(targetDow, h, m, s) {
  const now = new Date();
  for (let i = 1; i <= 8; i++) {
    const probe = new Date(now.getTime() + i * 86_400_000);
    const nzWeekday = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ, weekday: 'short',
    }).format(probe);
    if (DOW[nzWeekday] !== targetDow) continue;

    const nzDateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(probe);

    return nzToUTC(nzDateStr, h, m, s);
  }
  return null;
}

class CountdownTimerComponent extends HTMLElement {
  connectedCallback() {
    this._deadline = this._parseDeadline();
    this._els = {
      display:  this.querySelector('.countdown-timer__display'),
      expired:  this.querySelector('.countdown-timer__expired'),
      days:     this.querySelector('[data-unit="days"]'),
      hours:    this.querySelector('[data-unit="hours"]'),
      minutes:  this.querySelector('[data-unit="minutes"]'),
      seconds:  this.querySelector('[data-unit="seconds"]'),
    };
    this._tick();
    this._interval = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  _parseDeadline() {
    const type   = this.dataset.deadline   || 'next-monday';
    const custom = this.dataset.customDate || '';

    if (type === 'custom' && custom) return new Date(custom);
    if (type === 'next-tuesday')     return nextNZDeadline(2, 11,  0,  0);
                                     return nextNZDeadline(1, 23, 59, 59); // next-monday
  }

  _tick() {
    if (!this._deadline) return;
    const remaining = this._deadline.getTime() - Date.now();

    if (remaining <= 0) {
      this._showExpired();
      clearInterval(this._interval);
      return;
    }

    const totalSecs = Math.floor(remaining / 1000);
    const days    = Math.floor(totalSecs / 86400);
    const hours   = Math.floor((totalSecs % 86400) / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;
    const pad = n => String(n).padStart(2, '0');

    if (this._els.days)    this._els.days.textContent    = pad(days);
    if (this._els.hours)   this._els.hours.textContent   = pad(hours);
    if (this._els.minutes) this._els.minutes.textContent = pad(minutes);
    if (this._els.seconds) this._els.seconds.textContent = pad(seconds);
  }

  _showExpired() {
    // Add class to component — CSS hides .countdown-timer__inner and
    // shows .countdown-timer__expired via countdown-timer-component.is-expired rules.
    this.classList.add('is-expired');
    if (this._els.expired) this._els.expired.removeAttribute('hidden');
  }
}

if (!customElements.get('countdown-timer-component')) {
  customElements.define('countdown-timer-component', CountdownTimerComponent);
}
